import { useState } from "react";

import { Badge } from "./ui/badge";


const REVIEW_STATUS_STYLES = {
  agreed: "bg-success-surface text-success-foreground",
  resolved_by_adjudication: "bg-success-surface text-success-foreground",
  confirmed_by_user: "bg-success-surface text-success-foreground",
  single_source: "bg-warning-surface text-warning-foreground",
  needs_review: "bg-danger-surface text-danger-foreground",
  disagreed: "bg-danger-surface text-danger-foreground",
  missing: "bg-secondary text-muted-foreground",
};

const REVIEW_STATUS_LABELS = {
  agreed: "Both models agreed",
  resolved_by_adjudication: "Resolved",
  confirmed_by_user: "Confirmed by user",
  single_source: "One source",
  needs_review: "Needs your decision",
  disagreed: "Disagreed",
  missing: "Not found",
};

/** Field-level document evidence shown before a governed write is approved. */
export default function ExtractionReview({ review }) {
  const [showAll, setShowAll] = useState(false);
  const rows = review?.fields || [];
  const unresolved = rows.filter((row) => row.status === "needs_review" || row.status === "disagreed");
  const visible = showAll ? rows : (unresolved.length ? unresolved : rows.slice(0, 6));

  return (
    <div className="space-y-2 rounded-lg border border-border bg-secondary/30 p-3" data-testid="extraction-review">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Extracted from {review?.document?.file_name || "document"}
        </span>
        <Badge className={`border-0 text-[9px] ${review?.verified ? "bg-success-surface text-success-foreground" : "bg-warning-surface text-warning-foreground"}`}>
          {review?.summary_label || "Review extraction"}
        </Badge>
        {review?.agreement && (
          <span className="text-[10px] text-muted-foreground">
            {review.agreement.fields_agreed_or_resolved}/{review.agreement.fields_total} fields confirmed
            {review.agreement.fields_confirmed_by_user > 0 && ` · ${review.agreement.fields_confirmed_by_user} confirmed by user`}
            {review.agreement.fields_needing_review > 0 && ` · ${review.agreement.fields_needing_review} need review`}
          </span>
        )}
      </div>

      {(review?.extractors || []).length > 0 && (
        <div className="font-mono text-[10px] text-muted-foreground">
          {review.extractors.map((item) => `${item.model_key} (${item.input_profile})`).join(" · ")}
        </div>
      )}

      {(review?.validation || []).length > 0 && (
        <ul className="space-y-1">
          {review.validation.map((item) => (
            <li key={item.check} className="rounded bg-danger-surface/40 px-2 py-1 text-[11px] text-danger-foreground">
              {item.detail}
            </li>
          ))}
        </ul>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <tbody>
            {visible.map((row) => (
              <tr key={row.field} className="align-top border-t border-border/60">
                <th scope="row" className="whitespace-nowrap py-1.5 pr-2 text-left font-normal text-muted-foreground">
                  {row.label}
                  {row.required && <span className="text-danger-foreground"> *</span>}
                </th>
                <td className="py-1.5 pr-2 text-foreground">
                  {row.source_resolution?.applied ? (
                    <div className="space-y-1">
                      {row.source_resolution.choices.map((choice) => (
                        <div key={choice.finding_index}>
                          <p className="font-medium">
                            {choice.field === "pickup.appointment" ? "Pickup appointment" : "Delivery appointment"}: {choice.candidate.value}
                          </p>
                          {choice.response_quotes?.map((quote) => (
                            <p key={quote} className="text-muted-foreground">Reply: “{quote}”</p>
                          ))}
                        </div>
                      ))}
                      <p className="text-muted-foreground">Only this appointment was clarified. Other checks still apply.</p>
                      <details>
                        <summary className="cursor-pointer text-primary">Original source readings</summary>
                        {row.original_candidates?.map((candidate, index) => (
                          <div key={index} className="mt-1 break-words">
                            <span className="text-muted-foreground">{candidate.model_key}{candidate.page ? ` · p${candidate.page}` : ""}: </span>
                            {typeof candidate.value === "object" ? JSON.stringify(candidate.value) : String(candidate.value)}
                          </div>
                        ))}
                      </details>
                    </div>
                  ) : row.competing_values?.length ? (
                    <div className="space-y-1">
                      {row.competing_values.map((candidate, index) => (
                        <div key={`${row.field}-${index}`} className="flex items-baseline gap-2">
                          <span className="font-medium">{String(candidate.value)}</span>
                          <span className="font-mono text-[10px] text-muted-foreground">
                            {candidate.model_key}{candidate.page ? ` · p${candidate.page}` : ""}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="font-medium">
                      {row.value === null || row.value === undefined || row.value === ""
                        ? "—"
                        : typeof row.value === "object" ? JSON.stringify(row.value) : String(row.value)}
                    </span>
                  )}
                </td>
                <td className="whitespace-nowrap py-1.5 pr-2">
                  <Badge className={`border-0 text-[9px] ${REVIEW_STATUS_STYLES[row.status] || "bg-secondary text-muted-foreground"}`}>
                    {REVIEW_STATUS_LABELS[row.status] || row.status}
                  </Badge>
                </td>
                <td className="whitespace-nowrap py-1.5 text-[10px] text-muted-foreground">
                  {row.page ? `p${row.page}` : ""}
                  {typeof row.confidence === "number" ? ` · ${Math.round(row.confidence * 100)}%` : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length > visible.length && (
        <button type="button" onClick={() => setShowAll(true)} className="text-[11px] text-primary hover:underline">
          Show all {rows.length} fields
        </button>
      )}
    </div>
  );
}
